import React, { useCallback, useMemo, useState } from "react";
import "../../pages/Dashboard.scss";

import { v4 as uuid } from "uuid";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "../../store";
import {
  setProcurementFilters,
  resetProcurementFilters,
} from "../../store/filtersSlice";
import agentIcon1 from "../../assets/images/agent_icon3.svg";
import agentIcon2 from "../../assets/images/agent_icon2.svg";
import agentIcon3 from "../../assets/images/agent_icon1.svg";
import agentIcon4 from "../../assets/images/agent_icon4.svg";
import agentIcon5 from "../../assets/images/agent_icon5.svg";
import { Modal } from "@mui/material";
import { Agents } from "../../types/master";

type AssociatedAgent = {
  id: string;
  name: string;
};

type WorkflowAgent = {
  id: string;
  name: string;
  description: string;
  iconType: "blue-circle" | "lightning" | "star";
  associatedAgents?: AssociatedAgent[];
  procurementType?: "reviewer"; // Only "reviewer" for agent 20's initial state
};

// Icon mapping for efficient rendering
const ICON_MAP: Record<WorkflowAgent["iconType"], string> = {
  "blue-circle": agentIcon1,
  lightning: agentIcon2,
  star: agentIcon3,
};

// Category-specific icon mapping
const CATEGORY_ICON_MAP: Record<string, string> = {
  "CEO's Office": agentIcon1, // agent_icon1.svg
  Marketing: agentIcon2, // agent_icon2.svg
  "Augmented HCC": agentIcon3, // agent_icon3.svg
  Procurement: agentIcon4, // agent_icon4.svg
  Legal: agentIcon5, // agent_icon5.svg
};

// Category-to-workflows mapping for efficient lookup
const WORKFLOWS_BY_CATEGORY: Record<string, WorkflowAgent[]> = {
  "CEO's Office": [
    {
      id: "31",
      name: "Smart Meeting Brief",
      description: "Smart Meeting Brief",
      iconType: "blue-circle",
      // associatedAgents: [
      //   { id: "a1", name: "Press Release Agent" },
      //   { id: "a2", name: "Brand Reviewer Agent" },
      //   { id: "a3", name: "Localizer Agent" },
      // ],
    },

    {
      id: "32",
      name: "Trend Watch: News and Tech",
      description: "Trend Watch: News and Tech",
      iconType: "blue-circle",
      // associatedAgents: [
      //   { id: "a1", name: "Press Release Agent" },
      //   { id: "a2", name: "Brand Reviewer Agent" },
      //   { id: "a3", name: "Localizer Agent" },
      // ],
    },
    {
      id: "33",
      name: "Executive Operator",
      description: "Executive Operator",
      iconType: "blue-circle",
      // associatedAgents: [
      //   { id: "a1", name: "Press Release Agent" },
      //   { id: "a2", name: "Brand Reviewer Agent" },
      //   { id: "a3", name: "Localizer Agent" },
      // ],
    },
    {
      id: "34",
      name: "Relationship Mapper",
      description: "Relationship Mapper",
      iconType: "blue-circle",
      // associatedAgents: [
      //   { id: "a1", name: "Press Release Agent" },
      //   { id: "a2", name: "Brand Reviewer Agent" },
      //   { id: "a3", name: "Localizer Agent" },
      // ],
    },
    {
      id: "35",
      name: "Autonomous News Scanner and Emailer (Space42)",
      description: "Autonomous News Scanner and Emailer (Space42)",
      iconType: "blue-circle",
    },
  ],
  Marketing: [
    {
      id: "11",
      name: "Press Release and Corporate Communications Agent",
      description: "Press Release and Corporate Communications Agent",
      iconType: "blue-circle",
      // associatedAgents: [
      //   { id: "a1", name: "Press Release Agent" },
      //   { id: "a2", name: "Brand Reviewer Agent" },
      //   { id: "a3", name: "Localizer Agent" },
      // ],
    },

    {
      id: "7",
      name: "Translator Agent",
      description: "Translator Agent",
      iconType: "star",
      // associatedAgents: [{ id: "a7", name: "Culture & Tone Agent" }],
    },

    {
      id: "106",
      name: "Social Media Agent (LinkedIn, Instagram, X)",
      description: "Social Media Agent (LinkedIn, Instagram, X)",
      iconType: "star",
      // associatedAgents: [{ id: "a14", name: "Current News Agent" }],
    },
    {
      id: "22",
      name: "Internal Comms Agent",
      description: "Internal Comms Agent",
      iconType: "star",
      // associatedAgents: [{ id: "a14", name: "Current News Agent" }],
    },
    {
      id: "4",
      name: "Press Release and Brand Tone Reviewer",
      description: "Press Release and Brand Tone Reviewer",
      iconType: "lightning",
      // associatedAgents: [{ id: "a4", name: "Brand Reviewer Agent" }],
    },
    {
      id: "9",
      name: "Media Brief Q&A Assistant",
      description: "Media Brief Q&A Assistant",
      iconType: "star",
      // associatedAgents: [
      //   { id: "a10", name: "X Agent" },
      //   { id: "a11", name: "LinkedIn Creator" },
      //   { id: "a12", name: "Instagram Creator" },
      //   { id: "a13", name: "Facebook Creator" },
      // ],
    },
  ],
  "Augmented HCC": [
    {
      id: "24",
      name: "Skill Inference Engine",
      description: "Skill Inference Engine",
      iconType: "star",
      // associatedAgents: [
      //   { id: "a14", name: "Synthetic Profile Agent" },
      //   { id: "a15", name: "Scoring Agent" },
      // ],
    },
    {
      id: "18",
      name: "Candidate Screener",
      description: " Candidate Screener",
      iconType: "star",
      // associatedAgents: [{ id: "a16", name: "Interview Agent" }],
    },
    {
      id: "30",
      name: "AI Agent Screener",
      description: " AI Agent Screener",
      iconType: "star",
      // associatedAgents: [{ id: "a16", name: "Interview Agent" }],
    },
    // {
    //   id: "191",
    //   name: "Historical Feedback Analyzer",
    //   description: "Historical Feedback Analyzer",
    //   iconType: "star",
    //   // associatedAgents: [{ id: "a16", name: "Interview Agent" }],
    // },

    {
      id: "19",
      name: "Interviewer Host",
      description: "Interviewer Host",
      iconType: "star",
      // associatedAgents: [{ id: "a16", name: "Interview Agent" }],
    },
    {
      id: "23",
      name: "JD Composer",
      description: "JD Composer",
      iconType: "star",
      // associatedAgents: [
      //   { id: "a14", name: "Synthetic Profile Agent" },
      //   { id: "a15", name: "Scoring Agent" },
      // ],
    },
    {
      id: "36",
      name: "Organization Intelligence Agent (Pre Beta)",
      description: "Organization Intelligence Agent (Pre Beta)",
      iconType: "star",
      // associatedAgents: [{ id: "a16", name: "Interview Agent" }],
    },
    {
      id: "196",
      name: "Candidate Fit Assessor",
      description: "Candidate Fit Assessor",
      iconType: "star",
      // associatedAgents: [{ id: "a16", name: "Interview Agent" }],
    },

    {
      id: "192",
      name: "Talent Tracker: Location and Company Insights",
      description: "Talent Tracker: Location and Company Insights",
      iconType: "star",
      // associatedAgents: [{ id: "a16", name: "Interview Agent" }],
    },
    {
      id: "193",
      name: "First-Round Interview Screener",
      description: "First-Round Interview Screener",
      iconType: "star",
      // associatedAgents: [{ id: "a16", name: "Interview Agent" }],
    },
    {
      id: "194",
      name: "Talent Reconnect Emailer",
      description: "Talent Reconnect Emailer",
      iconType: "star",
      // associatedAgents: [{ id: "a16", name: "Interview Agent" }],
    },
    {
      id: "195",
      name: "Employee Intelligence Agent",
      description: "Employee Intelligence Agent",
      iconType: "star",
      // associatedAgents: [{ id: "a16", name: "Interview Agent" }],
    },
  ],
  Procurement: [
    {
      id: "21",
      name: "Contract Request (SOW Creator) Agent",
      description: "Contract Request (SOW Creator) Agent",
      iconType: "blue-circle",
    },
    {
      id: "20",
      name: "Contract Review, Negotiation and Finalization Agent",
      description: "Contract Review, Negotiation and Finalization Agent",
      iconType: "blue-circle",
      procurementType: "reviewer", // Starts with reviewer mode
    },
    {
      id: "46",
      name: "Template Review (MSA) Agent",
      description: "Template Review (MSA) Agent",
      iconType: "blue-circle",
      procurementType: "reviewer", // Starts with reviewer mode
    },
    {
      id: "201",
      name: "Contract Reviewer: Legal Terms",
      description: "Contract Reviewer: Legal Terms",
      iconType: "blue-circle",
    },
    {
      id: "202",
      name: "Contract Reviewer: Finance Terms",
      description: "Contract Reviewer: Finance Terms",
      iconType: "blue-circle",
    },
    {
      id: "203",
      name: "Contract Reviewer: Procurement Terms",
      description: "Contract Reviewer: Procurement Terms",
      iconType: "blue-circle",
    },
    {
      id: "204",
      name: "Contract Reviewer: Business Terms",
      description: "Contract Reviewer: Business Terms",
      iconType: "blue-circle",
    },
    {
      id: "205",
      name: "Contract Reviewer: Review Consolidator and Negotiation List Builder",
      description:
        "Contract Reviewer: Review Consolidator and Negotiation List Builder",
      iconType: "blue-circle",
    },
  ],
  Legal: [
    {
      id: "301",
      name: "Legal Translator",
      description: "Legal Translator",
      iconType: "blue-circle",
    },
    {
      id: "302",
      name: "Legal Procurement Review",
      description: "Legal Procurement Review",
      iconType: "blue-circle",
    },
  ],
  Commercial: [
    {
      id: "1001",
      name: "RFP Responder",
      description: "RFP Responder",
      iconType: "blue-circle",
    },
  ],
};

// Extract categories from the workflow mapping to ensure consistency
const CATEGORIES = Object.keys(WORKFLOWS_BY_CATEGORY);

type WorkflowsByFunctionProps = {
  onAgentClick: (agentId: number) => void;
};

export default function WorkflowsByFunction({
  onAgentClick,
}: WorkflowsByFunctionProps): JSX.Element {
  const [activeCategory, setActiveCategory] = useState(CATEGORIES[0]);
  const allAgents = useSelector((state: RootState) => state.agents.data);
  const dispatch = useAppDispatch();

  // Helper function to construct video blob URL
  const getVideoUrl = useCallback(
    (workflowId: string): string | null => {
      const agent = allAgents.find((a) => a.id === Number(workflowId));
      if (!agent?.agent_intro_video_path) return null;
      return `${import.meta.env.VITE_TEMPLATE_BLOB_URL}/intro-videos/${agent.agent_intro_video_path}?${import.meta.env.VITE_TEMPLATE_G42_FILES}`;
    },
    [allAgents],
  );

  // Get workflows for the active category efficiently
  const currentWorkflows = useMemo(
    () => WORKFLOWS_BY_CATEGORY[activeCategory] || [],
    [activeCategory],
  );

  // Helper function to check if agent exists and is available
  const isAgentAvailable = useCallback(
    (workflowId: string) => {
      const agent = allAgents.find((a) => a.id === Number(workflowId));
      if (!agent) return false;

      // Agent IDs that should open using their endpoint URL in a new tab
      const EXTERNAL_ENDPOINT_AGENTS = [19, 31, 32, 33, 34];

      // If agent requires external endpoint but doesn't have one, it's not available
      if (
        EXTERNAL_ENDPOINT_AGENTS.includes(Number(workflowId)) &&
        !agent.endpoint
      ) {
        return false;
      }

      return true;
    },
    [allAgents],
  );

  // Memoized icon rendering function
  const getIcon = useCallback(
    (iconType: WorkflowAgent["iconType"], category?: string) => {
      // Use category-specific icon if available, otherwise use iconType mapping
      const iconSrc =
        category && CATEGORY_ICON_MAP[category]
          ? CATEGORY_ICON_MAP[category]
          : ICON_MAP[iconType];
      return <img src={iconSrc} alt={`${iconType} icon`} />;
    },
    [],
  );

  const handleSelectAgent = useCallback(
    (id: number, procurementType?: "reviewer") => {
      // Set procurement type for agent 20 to start with "reviewer"
      if (procurementType) {
        dispatch(setProcurementFilters({ procurementType }));
      } else {
        // Reset procurement type for other agents (like agent 21)
        dispatch(resetProcurementFilters());
      }
      onAgentClick(id);
    },
    [onAgentClick, dispatch],
  );

  const [openVideoWorkflowId, setOpenVideoWorkflowId] = useState<string | null>(
    null,
  );

  const handleVideoClick = (workflowId: string) => {
    setOpenVideoWorkflowId(workflowId);
  };

  const closeVideoModal = () => {
    setOpenVideoWorkflowId(null);
  };

  return (
    <div className="card workflows">
      <div className="card-header">
        <h3 style={{ fontFamily: "G42 Sans Black Regular" }}>
          Agents by Function
        </h3>
      </div>
      <p className="subtle">
        Explore reimagined processes for your function powered by agents.
      </p>

      <div className="tabs">
        {CATEGORIES.map((category) => (
          <button
            key={category}
            className={`tab ${activeCategory === category ? "active" : ""}`}
            onClick={() => setActiveCategory(category)}
          >
            {category}
          </button>
        ))}
      </div>

      <div className="workflow-grid">
        {currentWorkflows.map((workflow, index) => (
          <div key={index} className="workflow-item">
            <div className="workflow-main">
              <div className="workflow-header">
                {getIcon(workflow.iconType, activeCategory)}
                <div className="workflow-info">
                  <div className="wf-title">{workflow.name}</div>
                  {workflow.name === "Candidate Screener" && (
                    <div className="wf-sub">
                      Candidate Fit Assessor + Talent Tracker: Location and
                      Company Insights + First-Round Interview Screener + Talent
                      Reconnect Emailer
                    </div>
                  )}
                  {workflow.name ===
                    "Contract Review, Negotiation and Finalization Agent" && (
                    <div className="wf-sub">
                      Contract Reviewer: Legal Terms + FinanceTerms +
                      Procurement Terms + Business Terms + Review Consolidator &
                      Negotiation List Builder + Redraft
                    </div>
                  )}
                  {workflow.name === "Contract Request (SOW Creator) Agent" && (
                    <div className="wf-sub">
                      Business & Commercial Checklist + Financial & Payment
                      Checklist + Procurement & Legal Checklist
                    </div>
                  )}
                </div>
              </div>
              {workflow.associatedAgents &&
                workflow.associatedAgents.length > 0 && (
                  <div className="workflow-connector">
                    <div className="connector-line"></div>
                    <div className="associated-agents">
                      {workflow.associatedAgents.map((agent) => (
                        <span key={agent.id} className="associated-agent">
                          {agent.name}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
            </div>
            <div className="workflow-actions">
              <button
                className="agent-video-icon workflow-video-trigger"
                onClick={() => handleVideoClick(workflow.id)}
                aria-label="Watch how it works"
                disabled={!getVideoUrl(workflow.id)}
                style={{
                  opacity: !getVideoUrl(workflow.id) ? 0.5 : 1,
                  cursor: !getVideoUrl(workflow.id) ? "not-allowed" : "pointer",
                }}
              >
                <span className="workflow-video-icon-tooltip">
                  How it works
                </span>
                ▶
              </button>

              <button
                className="deploy"
                onClick={() =>
                  handleSelectAgent(
                    Number(workflow.id),
                    workflow.procurementType,
                  )
                }
                disabled={!isAgentAvailable(workflow.id)}
                style={{
                  opacity: !isAgentAvailable(workflow.id) ? 0.5 : 1,
                  cursor: !isAgentAvailable(workflow.id)
                    ? "not-allowed"
                    : "pointer",
                }}
              >
                <span>Launch</span>
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M6 12L10 8L6 4"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>
          </div>
        ))}
      </div>

      <Modal
        open={openVideoWorkflowId !== null}
        onClose={closeVideoModal}
        className="agent-video-modal"
        aria-labelledby="workflow-video-modal-title"
        aria-describedby="workflow-video-modal-description"
        slotProps={{
          backdrop: {
            sx: { backgroundColor: "rgba(0, 0, 0, 0.6)" },
          },
        }}
      >
        <div className="agent-video-modal-content">
          <button
            className="agent-video-modal-close"
            onClick={(e) => {
              e.stopPropagation();
              closeVideoModal();
            }}
            aria-label="Close video modal"
          >
            ✕
          </button>
          {openVideoWorkflowId !== null && (
            <video
              src={getVideoUrl(openVideoWorkflowId) || ""}
              controls
              autoPlay
              className="agent-video-modal-player"
            />
          )}
        </div>
      </Modal>
    </div>
  );
}
